import { memo, useCallback, useMemo, useState, type FormEvent } from 'react';

import { authCredentialsSchema, type TAuthCredentials } from '../../shared';

interface IAuthPanelProps {
  isBusy: boolean;
  onLogin: (credentials: TAuthCredentials) => Promise<void>;
  onRegister: (credentials: TAuthCredentials) => Promise<void>;
  onGoogleLogin: () => Promise<void>;
}

const AuthPanelComponent = ({
  isBusy,
  onLogin,
  onRegister,
  onGoogleLogin
}: IAuthPanelProps) => {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const isLoginMode = mode === 'login';

  const submitLabel = useMemo(() => {
    return isLoginMode ? 'Log in' : 'Create account';
  }, [isLoginMode]);

  const modeLabel = useMemo(() => {
    return isLoginMode ? 'Need an account?' : 'Already have an account?';
  }, [isLoginMode]);

  const modeActionLabel = useMemo(() => {
    return isLoginMode ? 'Sign up' : 'Log in';
  }, [isLoginMode]);

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const parsedCredentials = authCredentialsSchema.safeParse({
        email,
        password
      });

      if (!parsedCredentials.success) {
        setLocalError(
          parsedCredentials.error.issues[0]?.message ??
            'Email or password is invalid.'
        );
        return;
      }

      setLocalError(null);
      if (isLoginMode) {
        await onLogin(parsedCredentials.data);
        return;
      }

      await onRegister(parsedCredentials.data);
    },
    [email, isLoginMode, onLogin, onRegister, password]
  );

  const switchMode = useCallback(() => {
    setMode((previousMode) =>
      previousMode === 'login' ? 'register' : 'login'
    );
    setLocalError(null);
  }, []);

  const handleGoogleLogin = useCallback(async () => {
    setLocalError(null);
    await onGoogleLogin();
  }, [onGoogleLogin]);

  return (
    <section className="surface-panel mx-auto w-full max-w-md">
      <h2 className="text-2xl font-semibold tracking-tight text-foreground">
        Welcome to Sharkord Domains
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Claim and manage up to 3 custom Sharkord subdomains in seconds.
      </p>

      <form className="mt-6 flex flex-col gap-3" onSubmit={onSubmit}>
        <label className="form-label" htmlFor="email">
          Email
        </label>
        <input
          id="email"
          autoComplete="email"
          className="form-input"
          disabled={isBusy}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="you@example.com"
          type="email"
          value={email}
        />

        <label className="form-label mt-1" htmlFor="password">
          Password
        </label>
        <input
          id="password"
          autoComplete={isLoginMode ? 'current-password' : 'new-password'}
          className="form-input"
          disabled={isBusy}
          minLength={8}
          onChange={(event) => setPassword(event.target.value)}
          placeholder="At least 8 characters"
          type="password"
          value={password}
        />

        {localError ? (
          <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground">
            {localError}
          </p>
        ) : null}

        <button className="primary-button mt-2" disabled={isBusy} type="submit">
          {isBusy ? 'Please wait...' : submitLabel}
        </button>
      </form>

      <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        <span>or</span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <button
        className="secondary-button w-full"
        disabled={isBusy}
        onClick={() => void handleGoogleLogin()}
        type="button"
      >
        Continue with Google
      </button>

      <p className="mt-4 text-center text-sm text-muted-foreground">
        {modeLabel}{' '}
        <button
          className="font-semibold text-foreground underline decoration-border underline-offset-4"
          onClick={switchMode}
          type="button"
        >
          {modeActionLabel}
        </button>
      </p>
    </section>
  );
};

export const AuthPanel = memo(AuthPanelComponent);
